/*
  Landing-page product templates: keeps the "Colour: Cognac" labels in sync with the
  selected variant, shows price per sq ft and keeps the size list ("1 sqft / 3 sqft / 5 sqft",
  each with its price per sq ft and saving) up to date, and runs the gallery and zoom viewer, sticky add to cart
  bar, product video and cross-sell tabs. Also drives the editorial sections below the product:
  colour tiles and the square-foot guide select options in the buy box, the "Up close" photos open
  the zoom viewer, and content fades in as it scrolls into view.
  Listens to the theme's jQuery `variantChange` event.
*/
(function() {
  var SQFT = /^\s*(\d+(?:\.\d+)?)\s*sq\.?\s*f(?:ee)?t\.?\s*$/i;

  function money(cents) {
    if (window.theme && theme.Currency && theme.strings && theme.strings.moneyFormat) {
      return theme.Currency.formatMoney(cents, theme.strings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // Sticky add to cart: shown once the main button has scrolled out of view, hidden over the footer.
  function initStickyAtc(container) {
    var bar = container.querySelector('[data-pl-sticky-atc]');
    var mainButton = container.querySelector('[id^="AddToCart--"]');
    if (!bar || !mainButton) return null;

    // Fixed elements inside the page container move with the nav drawer transform, so live on <body>.
    document.body.appendChild(bar);
    bar.hidden = false;

    var button = bar.querySelector('[data-pl-sticky-button]');
    var priceEl = bar.querySelector('[data-pl-sticky-price]');
    var variantEl = bar.querySelector('[data-pl-sticky-variant]');
    var imageEl = bar.querySelector('[data-pl-sticky-image]');
    var footer = document.querySelector('.site-footer, footer');
    var pastButton = false;
    var footerVisible = false;

    function render() {
      var show = pastButton && !footerVisible;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      button.tabIndex = show ? 0 : -1;
      document.documentElement.classList.toggle('pl-sticky-atc-visible', show);
    }

    // A cheap scroll check rather than IntersectionObserver, so jumps (anchor links, restored
    // scroll position on reload) that skip over the button still show the bar. Runs straight
    // from the scroll event, which browsers already fire at most once per frame.
    function check() {
      var wasPast = pastButton;
      var wasOverFooter = footerVisible;
      // A hidden Add to cart (a quote app replacing it, e.g. the shoemaking courses) means no bar either.
      pastButton = mainButton.getClientRects().length > 0 && mainButton.getBoundingClientRect().bottom < 0;
      footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      if (pastButton !== wasPast || footerVisible !== wasOverFooter) render();
    }
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);

    button.addEventListener('click', function() {
      mainButton.click();
    });

    check();
    render();

    return function update(variant, product) {
      if (!variant) {
        button.disabled = true;
        return;
      }
      priceEl.innerHTML = money(variant.price);
      variantEl.textContent = product.variants.length > 1 ? variant.title : product.title;
      if (imageEl && variant.featured_image) {
        imageEl.src = variant.featured_image.src + (variant.featured_image.src.indexOf('?') > -1 ? '&' : '?') + 'width=96&height=96&crop=center';
      }
      // Mirror the main button once the theme has updated it (Add to cart / Sold out).
      setTimeout(function() {
        button.disabled = mainButton.disabled;
        var label = mainButton.querySelector('.btn__text');
        if (label) button.textContent = label.textContent.trim();
      }, 0);
    };
  }

  // Long descriptions are capped with a "Read more" toggle so the buy box and photos stay balanced.
  function initReadMore(container) {
    var description = container.querySelector('.product-single__description');
    if (!description) return;

    // Long descriptions arrive capped from Liquid (no layout shift); un-cap if it fits after all.
    if (!description.classList.contains('pl-clamp')) {
      if (description.scrollHeight < 480) return;
      description.classList.add('pl-clamp');
    }
    if (description.scrollHeight <= description.clientHeight + 40) {
      description.classList.remove('pl-clamp');
      return;
    }

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'pl-readmore';
    toggle.textContent = 'Read more';
    toggle.setAttribute('aria-expanded', 'false');
    description.insertAdjacentElement('afterend', toggle);

    toggle.addEventListener('click', function() {
      var expanded = description.classList.toggle('is-expanded');
      toggle.textContent = expanded ? 'Read less' : 'Read more';
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (!expanded) description.scrollIntoView({ block: 'nearest' });
    });
  }

  // Gallery: scroll-snap main track, thumbnails, arrows and the full-screen zoom viewer.
  function initGallery(container) {
    var gallery = container.querySelector('[data-pl-gallery]');
    if (!gallery) return null;

    var track = gallery.querySelector('[data-pl-gallery-track]');
    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-pl-slide]'));
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-pl-thumb]'));
    var thumbList = gallery.querySelector('[data-pl-thumbs]');
    var countEl = gallery.querySelector('[data-pl-count]');
    var prev = gallery.querySelector('[data-pl-prev]');
    var next = gallery.querySelector('[data-pl-next]');
    var current = -1;

    function setActive(index) {
      if (index === current) return;
      current = index;
      thumbs.forEach(function(thumb, i) {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (countEl) countEl.textContent = index + 1;
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;

      var thumb = thumbs[index];
      if (thumb && thumbList && thumbList.scrollWidth > thumbList.clientWidth) {
        thumbList.scrollTo({ left: thumb.parentNode.offsetLeft - (thumbList.clientWidth - thumb.offsetWidth) / 2, behavior: 'smooth' });
      }
      slides.forEach(function(slide, i) {
        var video = slide.querySelector('video');
        if (i !== index && video && !video.paused) video.pause();
      });
    }

    // While a programmatic (thumbnail / arrow / variant) scroll glides into place, the scroll
    // position doesn't reflect the chosen slide yet, so it mustn't overwrite the active index.
    var settleUntil = 0;

    function goTo(index, smooth) {
      index = Math.max(0, Math.min(slides.length - 1, index));
      settleUntil = Date.now() + 700;
      track.scrollTo({ left: slides[index].offsetLeft, behavior: smooth === false ? 'auto' : 'smooth' });
      setActive(index);
    }

    var scrollTimer;
    track.addEventListener('scroll', function() {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function() {
        if (Date.now() < settleUntil) return;
        setActive(Math.round(track.scrollLeft / track.clientWidth));
      }, 60);
    }, { passive: true });

    thumbs.forEach(function(thumb, i) {
      thumb.addEventListener('click', function() { goTo(i); });
    });
    if (prev) prev.addEventListener('click', function() { goTo(current - 1); });
    if (next) next.addEventListener('click', function() { goTo(current + 1); });
    track.addEventListener('keydown', function(evt) {
      if (evt.key === 'ArrowLeft') { evt.preventDefault(); goTo(current - 1); }
      if (evt.key === 'ArrowRight') { evt.preventDefault(); goTo(current + 1); }
    });

    goTo(parseInt(gallery.getAttribute('data-start-index'), 10) || 0, false);

    function indexOfMedia(list, mediaId) {
      for (var i = 0; i < list.length; i++) {
        if (list[i].getAttribute('data-media-id') === String(mediaId)) return i;
      }
      return -1;
    }

    // Full-screen viewer: tap/click to zoom 2.5x, then move the mouse or drag to pan.
    var dialog = gallery.querySelector('[data-pl-lightbox]');
    var openViewer = null;
    if (dialog && typeof dialog.showModal === 'function') {
      document.body.appendChild(dialog);
      var lbTrack = dialog.querySelector('[data-pl-lightbox-track]');
      var lbSlides = Array.prototype.slice.call(lbTrack.children);
      var lbCount = dialog.querySelector('[data-pl-lightbox-count]');
      var lbHint = dialog.querySelector('[data-pl-lightbox-hint]');
      var lbPrev = dialog.querySelector('[data-pl-lightbox-prev]');
      var lbNext = dialog.querySelector('[data-pl-lightbox-next]');
      var lbThumbs = Array.prototype.slice.call(dialog.querySelectorAll('[data-pl-lightbox-thumb]'));
      var lbThumbList = dialog.querySelector('[data-pl-lightbox-thumbs]');
      var lbCurrent = 0;
      var ZOOM = 2.5;

      var unzoom = function() {
        dialog.classList.remove('is-zoomed');
        lbSlides.forEach(function(slide) {
          var img = slide.querySelector('img');
          img.classList.remove('is-zoomed');
          img.style.transformOrigin = '';
        });
      };

      var lbSet = function(index) {
        if (index !== lbCurrent) unzoom();
        lbCurrent = index;
        lbCount.textContent = index + 1;
        if (lbPrev) lbPrev.disabled = index === 0;
        if (lbNext) lbNext.disabled = index === lbSlides.length - 1;
        lbThumbs.forEach(function(thumb, i) {
          if (i === index) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
        var activeThumb = lbThumbs[index];
        if (activeThumb && lbThumbList && lbThumbList.scrollWidth > lbThumbList.clientWidth) {
          lbThumbList.scrollTo({ left: activeThumb.parentNode.offsetLeft - (lbThumbList.clientWidth - activeThumb.offsetWidth) / 2, behavior: 'smooth' });
        }
        [index - 1, index, index + 1].forEach(function(k) {
          var img = lbSlides[k] && lbSlides[k].querySelector('img');
          if (img && !img.getAttribute('src')) img.src = img.getAttribute('data-src');
        });
      };

      var lbSettleUntil = 0;

      var lbGo = function(index, smooth) {
        index = Math.max(0, Math.min(lbSlides.length - 1, index));
        lbSettleUntil = Date.now() + 700;
        lbTrack.scrollTo({ left: lbSlides[index].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
        lbSet(index);
      };

      var lbScrollTimer;
      lbTrack.addEventListener('scroll', function() {
        clearTimeout(lbScrollTimer);
        lbScrollTimer = setTimeout(function() {
          if (Date.now() < lbSettleUntil) return;
          lbSet(Math.round(lbTrack.scrollLeft / lbTrack.clientWidth));
        }, 60);
      }, { passive: true });

      openViewer = function(mediaId) {
        var index = indexOfMedia(lbSlides, mediaId);
        if (index < 0) return false;
        dialog.showModal();
        document.documentElement.classList.add('pl-lightbox-open');
        lbGo(index, false);
        return true;
      };

      gallery.querySelectorAll('[data-pl-zoom]').forEach(function(button) {
        button.addEventListener('click', function() {
          if (!openViewer(button.getAttribute('data-pl-zoom'))) openViewer(lbSlides[0].getAttribute('data-media-id'));
        });
      });

      dialog.addEventListener('close', function() {
        unzoom();
        document.documentElement.classList.remove('pl-lightbox-open');
        var mainIndex = indexOfMedia(slides, lbSlides[lbCurrent].getAttribute('data-media-id'));
        if (mainIndex > -1) goTo(mainIndex, false);
      });
      dialog.querySelector('[data-pl-lightbox-close]').addEventListener('click', function() { dialog.close(); });
      lbThumbs.forEach(function(thumb, i) {
        thumb.addEventListener('click', function() { lbGo(i, true); });
      });
      // Clicking anywhere outside the photo and the controls closes the viewer.
      dialog.addEventListener('click', function(evt) {
        if (!evt.target.closest('.pl-lightbox__img, .pl-lightbox__arrow, .pl-lightbox__thumbs, .pl-lightbox__close')) dialog.close();
      });
      if (lbPrev) lbPrev.addEventListener('click', function() { lbGo(lbCurrent - 1, true); });
      if (lbNext) lbNext.addEventListener('click', function() { lbGo(lbCurrent + 1, true); });
      dialog.addEventListener('keydown', function(evt) {
        if (evt.key === 'ArrowLeft') { evt.preventDefault(); lbGo(lbCurrent - 1, true); }
        if (evt.key === 'ArrowRight') { evt.preventDefault(); lbGo(lbCurrent + 1, true); }
      });

      lbSlides.forEach(function(slide) {
        var img = slide.querySelector('img');
        var drag = null;

        var setOrigin = function(xPercent, yPercent) {
          img.style.transformOrigin = Math.max(0, Math.min(100, xPercent)) + '% ' + Math.max(0, Math.min(100, yPercent)) + '%';
        };

        img.addEventListener('click', function(evt) {
          if (drag && drag.moved) return;
          if (img.classList.contains('is-zoomed')) {
            unzoom();
            return;
          }
          var rect = img.getBoundingClientRect();
          setOrigin((evt.clientX - rect.left) / rect.width * 100, (evt.clientY - rect.top) / rect.height * 100);
          img.classList.add('is-zoomed');
          dialog.classList.add('is-zoomed');
          if (lbHint) lbHint.hidden = true;
        });

        // Mouse: the zoomed image follows the pointer.
        slide.addEventListener('mousemove', function(evt) {
          if (!img.classList.contains('is-zoomed')) return;
          var rect = slide.getBoundingClientRect();
          setOrigin((evt.clientX - rect.left) / rect.width * 100, (evt.clientY - rect.top) / rect.height * 100);
        });

        // Touch: drag to pan while zoomed.
        img.addEventListener('pointerdown', function(evt) {
          if (evt.pointerType === 'mouse') { drag = null; return; }
          var origin = (img.style.transformOrigin || '50% 50%').split(' ');
          drag = { x: evt.clientX, y: evt.clientY, ox: parseFloat(origin[0]), oy: parseFloat(origin[1]), moved: false };
        });
        img.addEventListener('pointermove', function(evt) {
          if (!drag || !img.classList.contains('is-zoomed')) return;
          var dx = evt.clientX - drag.x;
          var dy = evt.clientY - drag.y;
          if (Math.abs(dx) + Math.abs(dy) > 6) drag.moved = true;
          setOrigin(drag.ox - dx / (ZOOM - 1) / img.offsetWidth * 100, drag.oy - dy / (ZOOM - 1) / img.offsetHeight * 100);
        });
        img.addEventListener('pointerup', function() {
          setTimeout(function() { drag = null; }, 0);
        });
      });
    }

    return {
      goTo: function(mediaId) {
        var index = indexOfMedia(slides, mediaId);
        if (index > -1) goTo(index);
      },
      // Opens the zoom viewer on a photo; without the viewer, shows it in the gallery instead.
      open: function(mediaId) {
        if (openViewer && openViewer(mediaId)) return;
        var index = indexOfMedia(slides, mediaId);
        if (index < 0) return;
        goTo(index, false);
        scrollToElement(gallery);
      }
    };
  }

  // The site header turns fixed once the page scrolls; this is how much of the screen it covers.
  function headerOffset() {
    var header = document.querySelector('.site-header');
    if (!header) return 0;
    var position = getComputedStyle(header).position;
    if (position !== 'fixed' && position !== 'sticky') return 0;
    return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
  }

  // Keeps the sticky photo column clear of the fixed header. Reads the header in the frame callback, after every
  // scroll handler has run (jquery.sticky fixes the header and pl-nav.js collapses it), so it's never a frame behind.
  function initHeaderOffset() {
    var last = -1;
    var queued = false;
    function sync() {
      queued = false;
      var offset = headerOffset();
      if (offset === last) return;
      last = offset;
      document.documentElement.style.setProperty('--pl-header-offset', offset + 'px');
    }
    function queue() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(sync);
    }
    sync();
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
  }

  // The photo column only stays in view while the buy box scrolls if that's worth it: when the buy box runs on for
  // a good while past the photos (a short pin just makes the photos stop and start), and when the photos fit
  // under the header (otherwise their thumbnails would be out of reach until the column ends).
  function initStickyPhotos(container) {
    var row = container.querySelector('.product-single');
    if (!row || !window.ResizeObserver) return;
    var photos = row.querySelector(':scope > .grid__item:not(.product-single__meta--wrapper)');
    var info = row.querySelector(':scope > .product-single__meta--wrapper');
    if (!photos || !info) return;
    var MIN_TRAVEL = 240;
    function check() {
      var travel = info.offsetHeight - photos.offsetHeight;
      var room = window.innerHeight - 60 - 48;
      row.classList.toggle('pl-photos-static', travel < MIN_TRAVEL || photos.offsetHeight > room);
    }
    var observer = new ResizeObserver(check);
    observer.observe(photos);
    observer.observe(info);
    window.addEventListener('resize', check);
  }

  function scrollToElement(el) {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scrolling down makes the header fixed, so allow for its height even if it isn't fixed yet.
    var header = document.querySelector('.site-header');
    var clearance = Math.max(headerOffset(), header ? header.offsetHeight : 0);
    var top = el.getBoundingClientRect().top + window.pageYOffset - clearance - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
  }

  // The main product on the page, used by the sections below it.
  var primary = null;

  // Keep the colour tiles and the square-foot guide in step with the variant chosen in the buy box.
  function syncPickers(variant) {
    if (!variant) return;
    document.querySelectorAll('.pl-colours__tile[data-pl-pick-option], .pl-hw-size[data-pl-pick-option]').forEach(function(tile) {
      var selected = pickValues(tile).indexOf(variant['option' + tile.getAttribute('data-pl-pick-option')]) !== -1;
      tile.classList.toggle('is-selected', selected);
      tile.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    document.querySelectorAll('[data-pl-sqft]').forEach(function(guide) {
      guide.querySelectorAll('[data-pl-sqft-panel]').forEach(function(panel) {
        var pick = panel.querySelector('[data-pl-pick-option]');
        if (pick && variant['option' + pick.getAttribute('data-pl-pick-option')] === pick.getAttribute('data-pl-pick-value')) {
          showSqftSize(guide, panel.getAttribute('data-pl-sqft-panel'));
        }
      });
    });
  }

  // A button's option value, plus other spellings of it (data-pl-pick-values="3/4\"|3/4''").
  function pickValues(pick) {
    var values = pick.getAttribute('data-pl-pick-values');
    return values ? values.split('|') : [pick.getAttribute('data-pl-pick-value')];
  }

  function showSqftSize(guide, index) {
    index = String(index);
    guide.querySelectorAll('[data-pl-sqft-size]').forEach(function(button) {
      button.setAttribute('aria-pressed', button.getAttribute('data-pl-sqft-size') === index ? 'true' : 'false');
    });
    guide.querySelectorAll('[data-pl-sqft-panel]').forEach(function(panel) {
      panel.hidden = panel.getAttribute('data-pl-sqft-panel') !== index;
    });
    guide.querySelectorAll('[data-pl-sqft-piece]').forEach(function(piece) {
      piece.classList.toggle('is-active', piece.getAttribute('data-pl-sqft-piece') === index);
    });
  }

  function initSqftGuides() {
    document.querySelectorAll('[data-pl-sqft]').forEach(function(guide) {
      guide.querySelectorAll('[data-pl-sqft-size]').forEach(function(button) {
        button.addEventListener('click', function() {
          showSqftSize(guide, button.getAttribute('data-pl-sqft-size'));
        });
      });
    });
  }

  // Buttons anywhere on the page that choose an option in the buy box, or open a product photo.
  function initPageLinks() {
    document.addEventListener('click', function(evt) {
      if (!primary) return;

      var pick = evt.target.closest('[data-pl-pick-option]');
      if (pick) {
        var position = pick.getAttribute('data-pl-pick-option');
        primary.selectValue(position, primary.bestValue(position, pickValues(pick)));
        var input = primary.container.querySelector('[name="option' + position + '"], select[data-index="option' + position + '"]');
        var group = input && input.closest('.product-form__item, .selector-wrapper, .radio-wrapper');
        var target = group || primary.container;
        scrollToElement(target);
        target.classList.remove('pl-flash');
        void target.offsetWidth;
        target.classList.add('pl-flash');
        return;
      }

      var media = evt.target.closest('[data-pl-open-media]');
      if (media && primary.openMedia) {
        primary.openMedia(media.getAttribute('data-pl-open-media'));
      }
    });
  }

  // Colour tiles section: show a few rows of a long list (81 paint colours) until "Show all".
  function initColourCollapse() {
    document.querySelectorAll('[data-pl-collapse-rows]').forEach(function(grid) {
      var more = grid.parentNode.querySelector('[data-pl-colours-more]');
      if (!more) return;
      var rows = parseInt(grid.getAttribute('data-pl-collapse-rows'), 10);
      var label = more.textContent.trim();
      var expanded = false;
      function apply() {
        var items = Array.prototype.slice.call(grid.children);
        items.forEach(function(li) { li.classList.remove('is-overflow'); });
        var tops = [];
        items.forEach(function(li) { if (tops.indexOf(li.offsetTop) === -1) tops.push(li.offsetTop); });
        tops.sort(function(a, b) { return a - b; });
        // Phones show the colours as a swipeable strip (one "row"), so nothing to collapse there.
        if (tops.length <= rows) {
          more.hidden = true;
          return;
        }
        more.hidden = false;
        more.textContent = expanded ? 'Show fewer' : label;
        if (expanded) return;
        items.forEach(function(li) { if (li.offsetTop >= tops[rows]) li.classList.add('is-overflow'); });
      }
      more.addEventListener('click', function() {
        expanded = !expanded;
        apply();
        if (!expanded) scrollToElement(grid);
      });
      window.addEventListener('resize', apply);
      apply();
    });
  }

  // Sections hide themselves when they don't fit a product, which can leave two bands of the same
  // colour touching; mark the second so it gets a hairline divider (an overlay, so nothing moves).
  function joinBands() {
    var previous = null;
    document.querySelectorAll('.shopify-section').forEach(function(wrapper) {
      if (!wrapper.offsetHeight) return;
      var band = wrapper.querySelector('.pl-band');
      var tone = band && (band.className.match(/pl-band--(cream|white|dark)/) || [])[1];
      if (tone && tone === previous) band.classList.add('pl-band--joined');
      previous = tone || null;
    });
  }

  // Content below the fold fades up as it scrolls into view. Anything already on screen (or above
  // it, after a reload partway down) is left alone, so nothing visible ever blinks out.
  function initReveal() {
    var items = document.querySelectorAll('[data-pl-reveal]');
    if (!items.length || !('IntersectionObserver' in window)) return;
    if (window.Shopify && Shopify.designMode) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('pl-reveal-pending');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });

    var fold = window.innerHeight;
    items.forEach(function(item) {
      if (item.getBoundingClientRect().top < fold) return;
      item.classList.add('pl-reveal-pending');
      observer.observe(item);
    });

    window.addEventListener('beforeprint', function() {
      items.forEach(function(item) { item.classList.remove('pl-reveal-pending'); });
    });
  }

  function init(container) {
    // The theme renders the product JSON just after the section container.
    var jsonEl = document.getElementById('ProductJson-' + container.getAttribute('data-section-id'));
    if (!jsonEl) return;

    var product = JSON.parse(jsonEl.textContent);
    var sizesEl = container.querySelector('[data-pl-sizes]');

    // "See sizes to scale" under the size list scrolls to the square-foot guide further down.
    // Hardware size guide link beside the Size label (sections/pl-hw-sizes.liquid)
    container.querySelectorAll('[data-pl-size-guide]').forEach(function(link) {
      var target = document.getElementById('hardware-sizes');
      if (!target) {
        link.hidden = true;
        return;
      }
      link.addEventListener('click', function(evt) {
        evt.preventDefault();
        scrollToElement(target);
      });
    });

    var guideLink = container.querySelector('[data-pl-sizes-guide]');
    if (guideLink) {
      var guide = document.getElementById('how-much-leather');
      if (!guide) {
        guideLink.hidden = true;
      } else {
        guideLink.addEventListener('click', function(evt) {
          evt.preventDefault();
          scrollToElement(guide);
        });
      }
    }
    var sizesHint = container.querySelector('[data-pl-sizes-hint]');
    var masterSelect = container.querySelector('select[name="id"]');

    function selectValue(position, value) {
      var input = container.querySelector('input[name="option' + position + '"][value="' + CSS.escape(value) + '"]');
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      var select = container.querySelector('select[data-index="option' + position + '"]');
      if (select) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Of several spellings of one value, the one that exists with the options already chosen
    // (in stock if possible).
    function bestValue(position, values) {
      if (values.length < 2) return values[0];
      // What's chosen now (read from the form: the combination may not exist as a variant)
      var chosen = {};
      for (var i = 1; i <= 3; i++) {
        var input = container.querySelector('input[name="option' + i + '"]:checked, select[data-index="option' + i + '"]');
        if (input) chosen[i] = input.value;
      }
      var exists = function(value, inStock) {
        return product.variants.some(function(v) {
          if (v['option' + position] !== value || (inStock && !v.available)) return false;
          for (var i = 1; i <= 3; i++) {
            if (String(i) !== String(position) && i in chosen && v['option' + i] !== chosen[i]) return false;
          }
          return true;
        });
      };
      return values.filter(function(v) { return exists(v, true); })[0] || values.filter(function(v) { return exists(v, false); })[0] || values[0];
    }

    // Size list (sections/product-template.liquid): each size's total, price per sq ft and saving
    // against the dearest price per sq ft, for the colour and weight currently chosen.
    // Packs: price per piece for the chosen pack, and the best saving on a bigger pack of the same
    // finish/size (in-stock packs only).
    var PACK = /^\s*(\d+)\s*(?:pcs?|pk|pack|pieces)?\s*$/i;
    function renderPacks(variant) {
      container.querySelectorAll('[data-pl-pack-each]').forEach(function(el) {
        var position = parseInt(el.getAttribute('data-pl-pack-each'), 10);
        var hint = container.querySelector('[data-pl-pack-hint="' + position + '"]');
        if (!variant) {
          el.textContent = '';
          if (hint) hint.textContent = '';
          return;
        }
        var count = function(v) {
          var m = PACK.exec(v['option' + position] || '');
          return m ? parseInt(m[1], 10) : 0;
        };
        var siblings = product.variants.filter(function(v) {
          for (var i = 1; i <= 3; i++) {
            if (i !== position && (v['option' + i] || null) !== (variant['option' + i] || null)) return false;
          }
          return count(v) > 0;
        });
        var n = count(variant);
        el.innerHTML = n ? money(Math.round(variant.price / n)) + ' each' : '';
        var each = siblings.map(function(v) { return v.price / count(v); });
        var most = Math.max.apply(null, each);
        var best = 0;
        siblings.forEach(function(v, i) {
          if (v.available && most) best = Math.max(best, Math.round((1 - each[i] / most) * 100));
        });
        if (hint) hint.textContent = best >= 1 ? 'Save up to ' + best + '% on bigger packs' : '';
      });
    }

    function renderSizes(variant) {
      if (!sizesEl || !variant) return;
      var position = parseInt(sizesEl.getAttribute('data-pl-sizes'), 10);
      var rows = Array.prototype.slice.call(sizesEl.querySelectorAll('[data-pl-size]')).map(function(label) {
        var value = label.getAttribute('data-pl-size');
        var match = product.variants.filter(function(v) {
          for (var i = 1; i <= 3; i++) {
            var wanted = i === position ? value : variant['option' + i];
            if ((v['option' + i] || null) !== (wanted || null)) return false;
          }
          return true;
        })[0];
        var parsed = SQFT.exec(value);
        var qty = parsed ? parseFloat(parsed[1]) : 0;
        return { label: label, match: match, unit: match && qty ? match.price / qty : 0 };
      });

      var base = rows.reduce(function(max, row) { return Math.max(max, row.unit); }, 0);
      var best = 0;
      rows.forEach(function(row) {
        var soldOut = !row.match || !row.match.available;
        var save = base && row.unit ? Math.round((1 - row.unit / base) * 100) : 0;
        if (!soldOut) best = Math.max(best, save);
        row.label.classList.toggle('is-sold-out', soldOut);
        row.label.querySelector('[data-pl-size-save]').textContent =
          !row.match ? 'Unavailable' : soldOut ? 'Sold out' : save >= 1 ? 'Save ' + save + '%' : '';
        row.label.querySelector('[data-pl-size-unit]').innerHTML = row.unit ? money(Math.round(row.unit)) + ' / sq ft' : '';
        row.label.querySelector('[data-pl-size-total]').innerHTML = row.match ? money(row.match.price) : '';
      });

      if (sizesHint) sizesHint.textContent = best >= 1 ? 'Save up to ' + best + '% on larger sizes' : '';
    }

    // Workshops: places left for the chosen date, and a full date's link to the other dates
    // (sections/product-template.liquid, sections/pl-workshop-dates.liquid).
    var spotsEl = container.querySelector('[data-pl-spots]');
    var spots = {};
    if (spotsEl) {
      try { spots = JSON.parse(spotsEl.getAttribute('data-pl-spots')) || {}; } catch (e) { spots = {}; }
    }
    function renderSpots(variant) {
      if (!spotsEl) return;
      var threshold = parseInt(spotsEl.getAttribute('data-pl-spots-threshold'), 10) || 3;
      var left = variant ? spots[variant.id] : undefined;
      var full = !!variant && !variant.available;
      var text = '';
      if (full) {
        text = spotsEl.getAttribute('data-pl-spots-full');
      } else if (typeof left === 'number' && left > 0 && left <= threshold) {
        text = left === 1 ? spotsEl.getAttribute('data-pl-spots-one') : spotsEl.getAttribute('data-pl-spots-many').replace('[count]', left);
      }
      spotsEl.textContent = text;
      var dates = document.getElementById('workshop-dates');
      if (full && dates && spotsEl.getAttribute('data-pl-spots-other')) {
        var link = document.createElement('a');
        link.href = '#workshop-dates';
        link.textContent = spotsEl.getAttribute('data-pl-spots-other');
        spotsEl.appendChild(document.createTextNode(' '));
        spotsEl.appendChild(link);
      }
      spotsEl.classList.toggle('is-full', full);
      spotsEl.hidden = !text;
    }

    // Add to cart shows price × quantity (so does the sticky bar).
    var currentVariant = null;
    var atcTotal = container.querySelector('[data-pl-atc-total]');
    var atcAmount = container.querySelector('[data-pl-atc-amount]');
    function renderTotal() {
      var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      var available = !!(currentVariant && currentVariant.available);
      var total = available ? money(currentVariant.price * qty) : '';
      if (atcTotal) {
        atcAmount.innerHTML = total;
        atcTotal.hidden = !available;
        // On a narrow phone a long total can't fit beside the label; the button then just says Add to cart.
        var button = atcTotal.closest('button');
        if (available && button && button.scrollWidth > button.clientWidth + 1) atcTotal.hidden = true;
      }
      var stickyPrice = document.querySelector('[data-pl-sticky-price]');
      if (stickyPrice && currentVariant) stickyPrice.innerHTML = money(currentVariant.price * qty);
    }

    // Long colour swatch lists (Angelus paint has 81) show three rows until "Show all" is pressed.
    container.querySelectorAll('[data-pl-swatch-collapse]').forEach(function(fieldset) {
      var more = fieldset.parentNode.querySelector('[data-pl-swatch-more]');
      if (!more) return;
      var label = more.textContent.trim();
      var expanded = false;
      function rowTops() {
        var tops = [];
        fieldset.querySelectorAll('label').forEach(function(l) {
          if (tops.indexOf(l.offsetTop) === -1) tops.push(l.offsetTop);
        });
        return tops.sort(function(a, b) { return a - b; });
      }
      function apply() {
        fieldset.style.maxHeight = '';
        fieldset.classList.remove('is-collapsed');
        var tops = rowTops();
        if (expanded || tops.length <= 3) {
          more.hidden = tops.length <= 3;
          more.textContent = expanded ? 'Show fewer' : label;
          return;
        }
        var checked = fieldset.querySelector('input:checked + label');
        // Never hide the chosen colour.
        if (checked && checked.offsetTop >= tops[3]) {
          expanded = true;
          return apply();
        }
        fieldset.style.maxHeight = (tops[3] - 4) + 'px';
        fieldset.classList.add('is-collapsed');
        more.hidden = false;
        more.textContent = label;
      }
      more.addEventListener('click', function() {
        expanded = !expanded;
        apply();
      });
      // Keyboard users arrowing into the list, or a colour picked elsewhere on the page (the colour
      // tiles section) that's hidden here, get the full list.
      function reveal() {
        if (!expanded && fieldset.classList.contains('is-collapsed')) {
          expanded = true;
          apply();
        }
      }
      fieldset.addEventListener('focusin', function(evt) {
        if (evt.target.matches(':focus-visible')) reveal();
      });
      fieldset.addEventListener('change', function() {
        var checked = fieldset.querySelector('input:checked + label');
        if (checked && checked.offsetTop + checked.offsetHeight > fieldset.clientHeight) reveal();
      });
      window.addEventListener('resize', apply);
      apply();
    });

    // Quantity stepper beside Add to cart.
    var stepper = container.querySelector('[data-pl-qty]');
    var qtyInput = stepper && stepper.querySelector('input');
    var qtyMinus = stepper && stepper.querySelector('[data-pl-qty-step="-1"]');
    function syncQty() {
      var value = parseInt(qtyInput.value, 10);
      if (!(value >= 1)) value = 1;
      if (String(value) !== qtyInput.value) qtyInput.value = value;
      qtyMinus.disabled = value <= 1;
      renderTotal();
    }
    if (stepper) {
      stepper.querySelectorAll('[data-pl-qty-step]').forEach(function(button) {
        button.addEventListener('click', function() {
          qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + parseInt(button.getAttribute('data-pl-qty-step'), 10));
          syncQty();
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      qtyInput.addEventListener('change', syncQty);
      qtyInput.addEventListener('blur', syncQty);
      qtyInput.addEventListener('input', renderTotal);
      window.addEventListener('resize', renderTotal);
    }

    var updateSticky = initStickyAtc(container);
    var gallery = initGallery(container);
    initStickyPhotos(container);
    var galleryGoTo = gallery && gallery.goTo;
    if (!primary) primary = { container: container, selectValue: selectValue, bestValue: bestValue, openMedia: gallery && gallery.open };
    var initialised = false;
    initReadMore(container);

    function update(variant) {
      if (updateSticky) updateSticky(variant, product);
      // No quantity to choose for a sold-out variant; Add to cart takes the full row.
      if (stepper) stepper.hidden = !(variant && variant.available);
      // Show the variant's photo (not on first load: the gallery already starts on it)
      if (galleryGoTo && initialised && variant && variant.featured_media) galleryGoTo(variant.featured_media.id);
      initialised = true;
      container.querySelectorAll('[data-pl-option-value]').forEach(function(el) {
        var position = el.getAttribute('data-pl-option-value');
        var checked = container.querySelector('input[name="option' + position + '"]:checked');
        var value = variant ? variant['option' + position] : checked ? checked.value : '';
        // Square-foot sizes read "6 sq ft", matching the size list.
        var size = SQFT.exec(value || '');
        el.textContent = size ? parseFloat(size[1]) + ' sq ft' : value;
      });
      renderSizes(variant);
      renderPacks(variant);
      renderSpots(variant);
      currentVariant = variant;
      renderTotal();
      if (primary && primary.container === container) syncPickers(variant);
    }

    var initialId = masterSelect ? parseInt(masterSelect.value, 10) : null;
    var initial = product.variants.filter(function(v) { return v.id === initialId; })[0] || product.variants[0];
    update(initial);

    if (window.jQuery) {
      jQuery(container).on('variantChange', function(evt) {
        update(evt.variant);
      });
    }
  }

  // Product video: fetch the Vimeo cover when the section nears the screen, load the player on click.
  // A video that no longer exists (deleted or made private) is removed, and its section with it.
  function dropVideo(wrapper) {
    var section = wrapper.closest('.pl-video-section');
    wrapper.remove();
    if (section && !section.querySelector('[data-pl-video], [data-pl-youtube]')) section.hidden = true;
  }

  function initVideos() {
    // YouTube videos linked in a description (a kit's tutorial). A missing video's thumbnail is a
    // 120×90 placeholder (or an error), so check it before anyone presses play.
    document.querySelectorAll('[data-pl-youtube]').forEach(function(wrapper) {
      var id = wrapper.getAttribute('data-pl-youtube');
      var button = wrapper.querySelector('.pl-video__play');
      var check = new Image();
      check.onload = function() { if (check.naturalWidth <= 120) dropVideo(wrapper); };
      check.onerror = function() { dropVideo(wrapper); };
      check.src = 'https://i.ytimg.com/vi/' + encodeURIComponent(id) + '/hqdefault.jpg';

      button.addEventListener('click', function() {
        var iframe = document.createElement('iframe');
        iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0&playsinline=1';
        iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
        iframe.allowFullscreen = true;
        iframe.title = button.getAttribute('aria-label');
        wrapper.innerHTML = '';
        wrapper.appendChild(iframe);
        wrapper.classList.add('is-playing');
      });
    });

    document.querySelectorAll('[data-pl-video]').forEach(function(wrapper) {
      var id = wrapper.getAttribute('data-pl-video');
      var button = wrapper.querySelector('.pl-video__play');
      var poster = wrapper.querySelector('[data-pl-video-poster]');

      function loadPoster() {
        fetch('https://vimeo.com/api/oembed.json?width=1280&url=' + encodeURIComponent('https://vimeo.com/' + id))
          .then(function(response) {
            if (response.status === 404) dropVideo(wrapper);
            return response.ok ? response.json() : null;
          })
          .then(function(data) {
            if (data && data.thumbnail_url) {
              poster.src = data.thumbnail_url;
              poster.hidden = false;
            }
          })
          .catch(function() {});
      }

      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function(entries) {
          if (entries[0].isIntersecting) {
            observer.disconnect();
            loadPoster();
          }
        }, { rootMargin: '600px 0px' });
        observer.observe(wrapper);
      } else {
        loadPoster();
      }

      button.addEventListener('click', function() {
        var iframe = document.createElement('iframe');
        iframe.src = 'https://player.vimeo.com/video/' + encodeURIComponent(id) + '?autoplay=1&title=0&byline=0&portrait=0';
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.title = button.getAttribute('aria-label');
        wrapper.innerHTML = '';
        wrapper.appendChild(iframe);
        wrapper.classList.add('is-playing');
      });
    });
  }

  // Collection tabs in the cross-sell section (arrow keys move between tabs).
  function initTabs() {
    document.querySelectorAll('[data-pl-tabs]').forEach(function(section) {
      var tabs = Array.prototype.slice.call(section.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;

      function select(tab, focus) {
        tabs.forEach(function(t) {
          var selected = t === tab;
          t.setAttribute('aria-selected', selected ? 'true' : 'false');
          t.tabIndex = selected ? 0 : -1;
          document.getElementById(t.getAttribute('aria-controls')).hidden = !selected;
        });
        if (focus) tab.focus();
      }

      tabs.forEach(function(tab, index) {
        tab.addEventListener('click', function() {
          select(tab);
        });
        tab.addEventListener('keydown', function(evt) {
          var next = null;
          if (evt.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
          if (evt.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
          if (next) {
            evt.preventDefault();
            select(next, true);
          }
        });
      });
    });
  }

  function start() {
    initSqftGuides();
    if (document.querySelector('[data-product-landing]')) initHeaderOffset();
    document.querySelectorAll('[data-product-landing]').forEach(init);
    initPageLinks();
    initVideos();
    initTabs();
    initColourCollapse();
    joinBands();
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
